import { QuestionBase, TextboxQuestion } from '@jump-tech-frontend/domain';
import { LikertQuestionParams } from './questions/likert/likert.model';
import { TextareaQuestionParams } from './questions/textarea/textarea.model';
import { TextboxQuestionParams } from './questions/textbox/textbox.model';
import { ModalQuestionParams } from './questions/modal/modal.model';
import { Questions } from './questions/question.model';
import { VideoCaptureQuestionParams } from './questions/video/video-capture.model';
import {
  ImageAnswer,
  ImageCaptureDevice,
  ImageCaptureQuestionParams
} from './questions/image-capture/image-capture.model';
import { OcrCaptureQuestionParams, OcrImageValue } from './questions/ocr-capture/ocr-capture.model';
import { ExampleImage } from './components/question-example-images.component';
import { ImageOrFileQuestionParams, ImageOrFileValue } from './questions/image-or-file/image-or-file.model';

export function getQuestionParams(question: QuestionBase<unknown>, currentQuestionValue: string): unknown {
  switch (question.controlType) {
    case Questions.TextareaQuestion:
      return cardQuestionToTextareaQuestionParamsTransformer(question as QuestionBase<string>, currentQuestionValue);
    case Questions.LikertQuestion:
      return cardQuestionToLikertQuestionParamsTransformer(question as QuestionBase<string>, currentQuestionValue);
    case Questions.ModalQuestion:
      return cardQuestionToModalQuestionParamsTransformer(question as QuestionBase<string>);
    case Questions.VideoQuestion:
      return cardQuestionToVideoQuestionParamsTransformer(question as QuestionBase<string[]>);
    default:
      return cardQuestionToTextboxQuestionParamsTransformer(question as TextboxQuestion, currentQuestionValue);
  }
}

export function getVideoQuestionParams(question: QuestionBase<string[]>, value: string[]): VideoCaptureQuestionParams {
  return cardQuestionToVideoQuestionParamsTransformer(question, value);
}

export interface ImageCaptureHostContext {
  deviceContext?: ImageCaptureDevice;
  projectId?: string;
  readonly?: boolean;
  examples?: ExampleImage[];
  qaHook?: string;
}

export type ImageCaptureQuestionConfig = Pick<
  QuestionBase<ImageAnswer[]>,
  'key' | 'label' | 'hint' | 'required' | 'value' | 'imageQualityCheck' | 'imageClassification' | 'sort'
>;

export function imageAnswerList<T>(value: T | T[] | null | undefined): T[] {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

export function imageAnswerSources(value: ImageAnswer | ImageAnswer[] | null | undefined): string[] {
  return imageAnswerList(value).map(answer => (typeof answer === 'string' ? answer : answer?.imageData));
}

export function getImageCaptureQuestionParams(
  question: ImageCaptureQuestionConfig,
  value: ImageAnswer | ImageAnswer[] | null | undefined,
  context: ImageCaptureHostContext = {}
): ImageCaptureQuestionParams {
  return {
    key: question.key,
    label: question.label,
    hint: question.hint,
    required: question.required,
    value: imageAnswerList(value ?? question.value),
    qualityCheck: question.imageQualityCheck,
    classification: question.imageClassification,
    sort: question.sort === 'asc' ? 'asc' : 'desc',
    ...context
  };
}

export type OcrCaptureQuestionConfig = Pick<QuestionBase<unknown>, 'key' | 'regexs' | 'targetOcr' | 'targetOcrField'>;

export function getOcrCaptureQuestionParams(
  question: OcrCaptureQuestionConfig,
  value: OcrImageValue | undefined,
  examples: ExampleImage[] = []
): OcrCaptureQuestionParams {
  return {
    key: question.key,
    value: value ?? null,
    targetOcr: question.targetOcr,
    targetOcrField: question.targetOcrField,
    patterns: question.regexs ?? [],
    examples
  };
}

export type ImageOrFileQuestionConfig = Pick<QuestionBase<unknown>, 'key' | 'accept' | 'sort'>;

export interface ImageOrFileHostContext {
  examples?: ExampleImage[];
  readonly?: boolean;
  qaHook?: string;
}

export function getImageOrFileQuestionParams(
  question: ImageOrFileQuestionConfig,
  value: ImageOrFileValue | undefined,
  context: ImageOrFileHostContext = {}
): ImageOrFileQuestionParams {
  return {
    key: question.key,
    value: value ?? null,
    accept: question.accept,
    sort: question.sort === 'desc' ? 'desc' : 'asc',
    examples: context.examples ?? [],
    readonly: context.readonly ?? false,
    qaHook: context.qaHook
  };
}

export function cardQuestionToTextboxQuestionParamsTransformer(
  question: TextboxQuestion,
  currentQuestionValue: string
): TextboxQuestionParams {
  return {
    key: question.key,
    label: question.label,
    required: question.required,
    value: currentQuestionValue ?? question.value,
    inline: question.inline
  };
}

export function cardQuestionToTextareaQuestionParamsTransformer(
  question: QuestionBase<string>,
  currentQuestionValue: string
): TextareaQuestionParams {
  return {
    key: question.key,
    label: question.label,
    required: question.required,
    value: currentQuestionValue ?? question.value
  };
}

export function cardQuestionToModalQuestionParamsTransformer(question: QuestionBase<string>): ModalQuestionParams {
  return {
    key: question.key,
    label: question.label,
    content: question.content,
    hint: question.hint,
    required: question.required,
    actionLabel: question.actionLabel
  };
}

export function cardQuestionToVideoQuestionParamsTransformer(
  question: QuestionBase<string[]>,
  value?: string[]
): VideoCaptureQuestionParams {
  return {
    key: question.key,
    label: question.label,
    hint: question.hint,
    required: question.required,
    value: value ?? question.value
  };
}

export function cardQuestionToLikertQuestionParamsTransformer(
  question: QuestionBase<string>,
  currentQuestionValue: string
): LikertQuestionParams {
  return {
    key: question.key,
    label: question.label,
    required: question.required,
    value: currentQuestionValue ?? question.value,
    bottomHint: question.bottomHint,
    topHint: question.topHint,
    steps: question.likertSteps
  };
}
